<template>
  <lila-default-template>
    <template #mainContent>
      <div class="journey container is-fluid">
        <header class="journey-page-header">
          <div>
            <h1 class="journey-page-title">
              {{ config.title }}
            </h1>
            <p class="journey-page-subtitle">
              {{ config.subtitle }}
            </p>
          </div>

          <button
            v-if="!isAmbulatory"
            type="button"
            class="journey-reset-button"
            title="Restaurar o exemplo inicial"
            @click="restoreExample"
          >
            Restaurar exemplo
          </button>
        </header>

        <p
          v-if="boardError"
          class="journey-board-error"
          role="alert"
        >
          {{ boardError }}
        </p>

        <div class="journey-board">
          <template
            v-for="(column, index) in board.columns"
            :key="column.id"
          >
            <section
              class="journey-column"
              :class="{ 'is-outcome': column.outcome }"
            >
              <header class="journey-column-header">
                <span class="journey-step-badge">{{ index + 1 }}</span>

                <input
                  v-if="editingColumnId === column.id"
                  ref="columnTitleInput"
                  v-model="column.title"
                  class="journey-column-title-input"
                  type="text"
                  @blur="stopEditingColumn"
                  @keyup.enter="stopEditingColumn"
                >
                <h2
                  v-else
                  class="journey-column-title"
                  :title="isAmbulatory ? '' : 'Clique duas vezes para renomear a etapa'"
                  @dblclick="!isAmbulatory && startEditingColumn(column.id)"
                >
                  {{ column.title }}
                </h2>

                <span
                  class="journey-column-count"
                  :title="`${column.cards.length} paciente(s) nesta etapa`"
                >
                  <svg
                    class="journey-column-count-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                    />
                  </svg>
                  {{ column.cards.length }}
                </span>

                <!-- Ambulatório: etapas fixas (são o local de cuidado do paciente) -->
                <button
                  v-if="!isAmbulatory"
                  type="button"
                  class="journey-column-remove"
                  title="Remover etapa"
                  @click="removeColumn(index)"
                >
                  ×
                </button>
              </header>

              <draggable
                :list="column.cards"
                group="journey-patients"
                item-key="id"
                class="journey-cards"
                ghost-class="journey-card--ghost"
                chosen-class="journey-card--chosen"
                drag-class="journey-card--dragging"
                :animation="180"
                @start="dragSourceColumnId = column.id"
                @change="onColumnChange(column, $event)"
              >
                <template #item="{ element: patient }">
                  <article
                    class="journey-card"
                    :style="{ borderLeftColor: specialtyColor(patient.specialty) }"
                    @click="openPatient(patient)"
                  >
                    <div class="journey-card-top">
                      <img
                        class="journey-card-avatar"
                        :src="patient.avatar"
                        :alt="`Foto de ${patient.name}`"
                        @error="useFallbackAvatar"
                      >
                      <div class="journey-card-identity">
                        <h3 class="journey-card-name">
                          {{ patient.name }}
                        </h3>
                        <span
                          v-if="patient.age != null"
                          class="journey-card-age"
                        >{{ patient.age }} anos</span>
                      </div>
                    </div>

                    <span
                      v-if="patient.specialty"
                      class="journey-card-specialty"
                      :style="{
                        color: specialtyColor(patient.specialty),
                        backgroundColor: specialtyBackground(patient.specialty)
                      }"
                    >
                      {{ patient.specialty }}
                    </span>

                    <p class="journey-card-summary">
                      {{ patient.summary }}
                    </p>

                    <!-- Ambulatório: desde quando está no local e funcionalidade (PPS) -->
                    <p
                      v-if="isAmbulatory"
                      class="journey-card-meta"
                    >
                      <template v-if="patient.locationSince">
                        {{ sinceLabel(column.kind) }} {{ formatSince(patient.locationSince) }}
                      </template>
                      <span v-if="patient.pps">{{ patient.locationSince ? " · " : "" }}PPS {{ patient.pps }}%</span>
                    </p>
                    <diagnosis-clock
                      v-else
                      :days="patient.daysSinceDiagnosis"
                    />
                  </article>
                </template>
              </draggable>
            </section>

            <div
              v-if="index < board.columns.length - 1"
              class="journey-connector"
              aria-hidden="true"
            >
              <span class="journey-connector-dot" />
              <span class="journey-connector-dot" />
              <span class="journey-connector-dot" />
              <span class="journey-connector-arrow">›</span>
            </div>
          </template>

          <section
            v-if="!isAmbulatory"
            class="journey-add-column"
          >
            <form
              v-if="isAddingColumn"
              class="journey-add-form"
              @submit.prevent="confirmAddColumn"
            >
              <input
                ref="newColumnInput"
                v-model="newColumnTitle"
                class="journey-add-input"
                type="text"
                placeholder="Nome da etapa"
                @keyup.esc="cancelAddColumn"
              >
              <div class="journey-add-actions">
                <button
                  type="submit"
                  class="journey-add-confirm"
                >
                  Adicionar
                </button>
                <button
                  type="button"
                  class="journey-add-cancel"
                  @click="cancelAddColumn"
                >
                  Cancelar
                </button>
              </div>
            </form>

            <button
              v-else
              type="button"
              class="journey-add-trigger"
              @click="startAddColumn"
            >
              <span class="journey-add-trigger-icon">+</span>
              Nova etapa
            </button>
          </section>
        </div>

        <!-- Ambulatório: ao soltar um card em "Em casa", confirma a mensagem de boas-vindas -->
        <div
          v-if="pendingHome"
          class="default-modal"
        >
          <div class="modal is-active">
            <div
              class="modal-background"
              @click="cancelHome"
            />
            <div
              class="modal-card journey-home-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="journey-home-title"
            >
              <header class="modal-card-head">
                <p
                  id="journey-home-title"
                  class="modal-card-title"
                >
                  Enviar {{ pendingHome.card.name }} para casa
                </p>
              </header>
              <section class="modal-card-body">
                <lila-home-welcome-composer
                  :patient-name="pendingHome.card.name"
                  @confirm="confirmHome"
                  @cancel="cancelHome"
                />
              </section>
            </div>
          </div>
        </div>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import draggable from "vuedraggable";
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import DiagnosisClock from "./DiagnosisClock.vue";
import {
  loadBoard,
  saveBoard,
  resetBoard,
  generateId
} from "./journeyStorage.js";
// POC Cuidados Paliativos: a mesma tela serve ao "Ambulatório" (rota com meta.board).
// No Ambulatório as 3 etapas são fixas e vêm da API, montadas pelo local de cuidado
// de cada paciente; mover um card muda o local (enviar para casa, internar, readmitir).
import HomeWelcomeComposer from "@components/patients/HomeWelcomeComposer.vue";
import {
  CARE_LOCATION,
  CARE_PLAN_UPDATED_EVENT,
  getAmbulatoryBoard,
  hospitalizePatient,
  readmitPatient,
  sendPatientHome
} from "@/services/palliative/carePlanApi";
import { eventBus } from "@/eventBus.js";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";

const FALLBACK_AVATAR = require("@/assets/images/default_avatar_neutral.png");
const DEFAULT_SPECIALTY_COLOR = "#7f8c8d";

const SPECIALTY_COLORS = {
  Cardiologia: "#e74c3c",
  Ortopedia: "#2980b9",
  Oncologia: "#8e44ad",
  Gastroenterologia: "#16a085",
  "Cirurgia Geral": "#e67e22"
};

const SINCE_LABELS = {
  [CARE_LOCATION.ambulatorio]: "No ambulatório desde",
  [CARE_LOCATION.casa]: "Em casa desde",
  [CARE_LOCATION.internacao]: "Internado desde"
};

const BOARDS = {
  jornada: {
    title: "Jornada do Paciente",
    subtitle: "Acompanhe cada paciente pelas etapas do cuidado — arraste os cards entre as colunas para avançar na jornada.",
    store: { loadBoard, saveBoard, resetBoard },
    colors: SPECIALTY_COLORS,
    patientRoute: "myPatientActions"
  },
  ambulatorio: {
    title: "Ambulatório",
    subtitle: "Pacientes da equipe pelo local de cuidado: no ambulatório, em casa com acompanhamento ou internados. Arraste os cards entre as etapas para mudar o local.",
    store: null,
    colors: {},
    // A aba Gestão do paciente tem o "Enviar para casa".
    patientRoute: "myPatientManagement"
  }
};

export default {
  components: {
    draggable,
    "lila-default-template": DefaultTemplate,
    "diagnosis-clock": DiagnosisClock,
    "lila-home-welcome-composer": HomeWelcomeComposer
  },
  // Ambulatório: local de cuidado mudado por outro profissional (ou na aba Gestão) move o card aqui.
  mixins: [pocLiveSync("syncCareLocations")],
  data() {
    return {
      // Nome do quadro, não o objeto: guardado em data, o objeto viraria um proxy
      // reativo e deixaria de ser igual ao de BOARDS.
      boardName: BOARDS[this.$route.meta.board] ? this.$route.meta.board : "jornada",
      board: { columns: [] },
      // Ambulatório: card solto em "Em casa" aguardando a mensagem de boas-vindas.
      dragSourceColumnId: null,
      pendingHome: null,
      boardBusy: false,
      boardError: "",
      editingColumnId: null,
      isAddingColumn: false,
      newColumnTitle: ""
    };
  },
  computed: {
    config() {
      return BOARDS[this.boardName];
    },
    isAmbulatory() {
      return this.boardName === "ambulatorio";
    }
  },
  watch: {
    // Jornada e Ambulatório usam esta mesma tela: ao trocar pelo menu, troca o quadro.
    "$route.meta.board"(boardName) {
      this.boardName = BOARDS[boardName] ? boardName : "jornada";
      this.loadCurrentBoard();
    },
    board: {
      handler(updatedBoard) {
        // O Ambulatório não é salvo: ele reflete o local de cuidado gravado na API.
        if (this.config.store) this.config.store.saveBoard(updatedBoard);
      },
      deep: true
    }
  },
  created() {
    this.loadCurrentBoard();
  },
  methods: {
    specialtyColor(specialty) {
      return this.config.colors[specialty] || DEFAULT_SPECIALTY_COLOR;
    },
    specialtyBackground(specialty) {
      return `${this.specialtyColor(specialty)}1a`;
    },
    useFallbackAvatar(event) {
      event.target.src = FALLBACK_AVATAR;
    },
    openPatient(patient) {
      if (this.isAmbulatory && !patient.patientId) return;
      this.$router.push({
        name: this.config.patientRoute,
        params: { id: patient.patientId || patient.id }
      });
    },
    loadCurrentBoard() {
      this.boardError = "";
      if (this.isAmbulatory) {
        this.board = { columns: [] };
        this.loadAmbulatoryBoard();
      } else {
        this.board = this.config.store.loadBoard();
      }
    },
    // Ambulatório: 3 etapas fixas vindas da API.
    async loadAmbulatoryBoard() {
      try {
        const columns = await getAmbulatoryBoard();
        if (!this.isAmbulatory) return;
        this.board = {
          columns: columns.map((column) => ({
            id: `amb-${column.kind}`,
            title: column.title,
            kind: column.kind,
            outcome: column.kind !== CARE_LOCATION.ambulatorio,
            cards: column.cards.map((card) => ({
              id: `patient-${card.patientId}`,
              patientId: card.patientId,
              name: card.name,
              age: card.age,
              avatar: card.photo || this.defaultAvatar(card),
              summary: card.summary,
              pps: card.pps,
              locationSince: card.locationSince
            }))
          }))
        };
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar o Ambulatório.", error);
        this.boardError = error.message;
      }
    },
    // O avatar padrão feminino é sorteado: guarda por paciente para não trocar a cada atualização.
    defaultAvatar(card) {
      this.avatarCache = this.avatarCache || {};
      if (!this.avatarCache[card.patientId]) {
        this.avatarCache[card.patientId] = this.$Utilities.getDefaultProfilePicture(card.gender) || FALLBACK_AVATAR;
      }
      return this.avatarCache[card.patientId];
    },
    sinceLabel(kind) {
      return SINCE_LABELS[kind] || "Desde";
    },
    formatSince(date) {
      return this.$moment(date).format("DD/MM [às] HH:mm");
    },
    // Ambulatório: as 3 etapas são o local de cuidado do paciente (o mesmo da aba Gestão).
    // Entrar em "Em casa" abre a mensagem; "Internação" e "Ambulatório" gravam na hora.
    onColumnChange(column, event) {
      if (!this.isAmbulatory || !event.added) return;
      const card = event.added.element;

      if (column.kind === CARE_LOCATION.casa) {
        this.pendingHome = { card, columnId: column.id, fromColumnId: this.dragSourceColumnId };
        return;
      }

      if (column.kind === CARE_LOCATION.internacao) {
        this.changeLocation(card, () => hospitalizePatient(card.patientId));
      } else if (column.kind === CARE_LOCATION.ambulatorio) {
        this.changeLocation(card, () => readmitPatient(card.patientId));
      }
    },
    // Grava a mudança e recarrega o quadro. Com erro, o quadro volta ao que está gravado.
    async changeLocation(card, action) {
      this.boardBusy = true;
      this.boardError = "";
      try {
        await action();
        eventBus.emit(CARE_PLAN_UPDATED_EVENT, { patientId: String(card.patientId) });
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível mudar o local de cuidado.", error);
        this.boardError = error.message;
      } finally {
        this.boardBusy = false;
      }
      await this.loadAmbulatoryBoard();
    },
    // Atualização periódica: recarrega o quadro, sem atrapalhar um envio para casa em andamento.
    syncCareLocations() {
      if (!this.isAmbulatory || this.pendingHome || this.boardBusy) return;
      this.loadAmbulatoryBoard();
    },
    confirmHome(message) {
      const { card } = this.pendingHome;
      this.pendingHome = null;
      this.changeLocation(card, () => sendPatientHome(card.patientId, message));
    },
    // Cancelar devolve o card para a etapa de onde ele veio.
    cancelHome() {
      const { card, columnId, fromColumnId } = this.pendingHome;
      const target = this.board.columns.find((c) => c.id === columnId);
      const source = this.board.columns.find((c) => c.id === fromColumnId);
      if (target && source) {
        target.cards = target.cards.filter((c) => c.id !== card.id);
        source.cards.push(card);
      }
      this.pendingHome = null;
    },
    startEditingColumn(columnId) {
      this.editingColumnId = columnId;
      this.$nextTick(() => {
        const input = this.resolveSingleRef("columnTitleInput");
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    stopEditingColumn() {
      this.editingColumnId = null;
    },
    removeColumn(columnIndex) {
      const column = this.board.columns[columnIndex];
      const hasCards = column.cards.length > 0;
      if (hasCards && !window.confirm(`Remover a etapa "${column.title}" e seus ${column.cards.length} card(s)?`)) {
        return;
      }
      this.board.columns.splice(columnIndex, 1);
    },
    startAddColumn() {
      this.isAddingColumn = true;
      this.newColumnTitle = "";
      this.$nextTick(() => {
        const input = this.resolveSingleRef("newColumnInput");
        if (input) input.focus();
      });
    },
    confirmAddColumn() {
      const title = this.newColumnTitle.trim();
      if (!title) return;
      this.board.columns.push({
        id: generateId("col"),
        title,
        cards: []
      });
      this.isAddingColumn = false;
      this.newColumnTitle = "";
    },
    cancelAddColumn() {
      this.isAddingColumn = false;
      this.newColumnTitle = "";
    },
    restoreExample() {
      if (!window.confirm("Restaurar a jornada para o exemplo inicial? As alterações locais serão descartadas.")) {
        return;
      }
      this.board = this.config.store.resetBoard();
    },
    resolveSingleRef(refName) {
      const reference = this.$refs[refName];
      return Array.isArray(reference) ? reference[0] : reference;
    }
  }
};
</script>

<style scoped lang="scss">
$lila-dark: #69287c;
$lila-medium: #8e44ad;

.journey {
  padding: 24px 28px 40px;
}

.journey-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.journey-page-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: $lila-dark;
  margin: 0;
}

.journey-page-subtitle {
  margin: 6px 0 0;
  color: #6b6b70;
  font-size: 0.95rem;
  max-width: 640px;
}

.journey-board-error {
  margin: 0 0 12px;
  color: #db466f;
  font-size: 0.9rem;
}

.journey-reset-button {
  flex-shrink: 0;
  border: 1px solid $lila-medium;
  background: #fff;
  color: $lila-medium;
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;

  &:hover {
    background: $lila-medium;
    color: #fff;
  }
}

.journey-board {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 8px 4px 20px;
  overflow-x: auto;
}

/* ---------- Coluna / etapa ---------- */
.journey-column {
  flex: 0 0 290px;
  width: 290px;
  background: #f4f2f7;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(105, 40, 124, 0.08);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 230px);
}

.journey-column h2 {
  margin-top: 0.2em;
}

.journey-column-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px;
  background: linear-gradient(135deg, $lila-dark, $lila-medium);
  border-radius: 14px 14px 0 0;
  color: #fff;
}

.journey-step-badge {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
}

.journey-column-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.2;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: text;
}

.journey-column-title-input {
  flex: 1;
  min-width: 0;
  font-size: 0.98rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  padding: 4px 8px;
  color: $lila-dark;
}

.journey-column-count {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
}

.journey-column-count-icon {
  width: 12px;
  height: 12px;
}

.journey-column-remove {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.journey-cards {
  flex: 1;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  min-height: 80px;
}

/* ---------- Card de paciente ---------- */
.journey-card {
  background: #fff;
  border-radius: 10px;
  border-left: 4px solid #ccc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  padding: 12px;
  cursor: pointer;
  transition: box-shadow 0.18s, transform 0.18s;

  &:hover {
    box-shadow: 0 4px 12px rgba(105, 40, 124, 0.18);
    transform: translateY(-2px);
  }
}

.journey-card--ghost {
  opacity: 0.45;
  background: #ece4f2;
}

.journey-card--chosen {
  cursor: grabbing;
}

.journey-card--dragging {
  box-shadow: 0 10px 24px rgba(105, 40, 124, 0.3);
  transform: rotate(2deg);
}

.journey-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.journey-card-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  background: #eee;
  flex-shrink: 0;
}

.journey-card-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.journey-card-name {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c2c30;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.journey-card-age {
  font-size: 0.78rem;
  color: #8a8a90;
}

.journey-card-specialty {
  display: inline-block;
  margin-top: 10px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}

.journey-card-meta {
  margin: 8px 0 0;
  font-size: 0.78rem;
  color: #8a8a90;
}

.journey-home-modal {
  max-width: 600px;
}

/* Colunas de destino (Ambulatório): cabeçalho claro, para separar da fila */
.journey-column.is-outcome .journey-column-header {
  background: #ffffff;
  color: $lila-dark;
  border: 2px solid $lila-medium;
  border-bottom: 0;

  .journey-column-title {
    color: $lila-dark;
  }

  .journey-step-badge {
    background: #f4f2f7;
    border-color: $lila-medium;
  }

  .journey-column-count {
    color: $lila-dark;
    background: #ece4f2;
  }

  .journey-column-remove {
    color: $lila-medium;
  }
}

.journey-card-summary {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: #555;
  line-height: 1.3;
}

/* ---------- Conector da jornada ---------- */
.journey-connector {
  flex: 0 0 46px;
  align-self: flex-start;
  margin-top: 10px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.journey-connector-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $lila-medium;
  opacity: 0.55;

  &:nth-child(2) {
    opacity: 0.75;
  }

  &:nth-child(3) {
    opacity: 1;
  }
}

.journey-connector-arrow {
  color: $lila-medium;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
  margin-left: 2px;
  margin-bottom: 2px;
}

/* ---------- Adicionar etapa ---------- */
.journey-add-column {
  flex: 0 0 250px;
  margin-left: 8px;
}

.journey-add-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border: 2px dashed #c4b3d0;
  border-radius: 14px;
  background: rgba(142, 68, 173, 0.05);
  color: $lila-medium;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s;

  &:hover {
    background: rgba(142, 68, 173, 0.12);
    border-color: $lila-medium;
  }
}

.journey-add-trigger-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.journey-add-form {
  background: #f4f2f7;
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 4px 14px rgba(105, 40, 124, 0.1);
}

.journey-add-input {
  width: 100%;
  border: 1px solid #d4c6dd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: $lila-medium;
  }
}

.journey-add-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.journey-add-confirm,
.journey-add-cancel {
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}

.journey-add-confirm {
  background: $lila-medium;
  color: #fff;
}

.journey-add-cancel {
  background: transparent;
  color: #777;
}
</style>
